import React, { useEffect, useMemo, useState } from "react";
import WebApp from "@twa-dev/sdk";

// Import modular components
import { Topbar } from "./components/Topbar";
import { Menu } from "./components/Menu";
import { LeadForm } from "./components/LeadForm";
import { LeadList } from "./components/LeadList";
import { LeadDetail } from "./components/LeadDetail";
import { AllLeads } from "./components/AllLeads";
import { Help } from "./components/Help";
import { Stats } from "./components/Stats";
import { AdminPanel } from "./components/AdminPanel";
import { EventsPanel } from "./components/EventsPanel";
import { AiScanner } from "./components/AiScanner";
import { PromptsManagement } from "./components/PromptsManagement";
import { Section, ToolbarButton } from "./components/UI";
import { ErrorDisplay } from "./components/ErrorDisplay";
// import { DebugPanel } from "./components/DebugPanel";

// Import utilities
import { getTelegramUser, getTelegramInitData, getTelegramStartParam, getManagerName } from "./utils/telegram";
import { exportCsv, uid } from "./utils/storage";
import { initTelegramWebApp } from "./utils/telegramInit";

// Import constants
import { emptyLead } from "./constants/leads";

// Import custom hook
import { useLeadsLogic } from "./hooks/useLeadsLogic";
import { useBackButton } from "./hooks/useBackButton";

// Import contexts
import { AuthProvider, useAuth } from "./contexts";
import { ToastProvider, useToast } from "./components/Toast";

// Initialize Telegram WebApp
initTelegramWebApp();

// Hard reload of the Mini App. Re-fetches index.html and the latest bundle,
// giving the user an escape hatch from any stuck/hung state.
function reloadApp() {
  try {
    window.location.reload();
  } catch (e) {
    console.error("Reload failed:", e);
  }
}

/**
 * Connection-trouble screen.
 * Shown when authentication did not produce a user — either because it is
 * taking too long (watchdog) or finished without a user (network/5xx error).
 * Always actionable (Обновить) so the app never looks like a silent hang.
 */
function ConnectionTrouble({ title, message, telegramId }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="text-6xl mb-4">📡</div>
        <h1 className="text-2xl font-bold mb-4">{title}</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
        <button
          onClick={reloadApp}
          className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors"
        >
          🔄 Обновить
        </button>
        {telegramId && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-6">
            <p>Если не помогает — закройте и откройте приложение заново.</p>
            <p className="mt-1">Ваш Telegram ID: <span className="font-mono">{telegramId}</span></p>
          </div>
        )}
      </div>
    </div>
  );
}

function AppContent() {
  // Get auth context
  const { user, loading: authLoading, error: authError, accessDenied } = useAuth();
  const toast = useToast();
  
  // Get Telegram user data (will throw error if not in Telegram)
  const telegramUser = useMemo(() => {
    try {
      return getTelegramUser();
    } catch (error) {
      console.error("Telegram initialization error:", error);
      return null;
    }
  }, []);
  
  const initData = useMemo(() => {
    try {
      return getTelegramInitData();
    } catch (error) {
      console.error("Telegram initData error:", error);
      return null;
    }
  }, []);
  
  // Show error if not running in Telegram
  if (!telegramUser || !initData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-4">Приложение должно быть запущено в Telegram</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Это Telegram Mini App, которое работает только внутри Telegram.
            Пожалуйста, откройте приложение через Telegram.
          </p>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>Для разработчиков: используйте ngrok или Telegram Bot Dev Tools</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Use backend user data
  const userRole = user?.role || "manager";
  
  const managerName = useMemo(() => {
    if (user) {
      return user.first_name && user.last_name 
        ? `${user.first_name} ${user.last_name.charAt(0)}.`
        : user.first_name || user.username || "Manager";
    }
    return getManagerName(telegramUser);
  }, [user, telegramUser]);
  
  const managerTg = useMemo(() => {
    if (user) {
      return user.username ? `@${user.username}` : `user_${user.telegram_id}`;
    }
    return telegramUser.username ? `@${telegramUser.username}` : `user_${telegramUser.id}`;
  }, [user, telegramUser]);
  
  const [role, setRole] = useState(userRole);
  
  // Update role when user data changes
  useEffect(() => {
    setRole(userRole);
  }, [userRole]);
  
  // Use custom hook for leads logic
  const {
    leads,
    setLeads,
    loading: leadsLoading,
    error: leadsError,
    view,
    setView,
    form,
    startAdd,
    startEdit,
    onChange,
    handleSave,
    handleSaveAndNew,
    handleSaveAndAI,
    handleDelete,
    handlePhotos,
    removePhoto,
    handleVoiceNoteUpload,
    handleVoiceNoteDelete,
    resetForm,
    loadSingleLead, // For deep linking
  } = useLeadsLogic(managerTg, managerName);

  // State for deep linking
  const [deepLinkLeadId, setDeepLinkLeadId] = useState(null);
  
  // Logging user information
  useEffect(() => {
    console.log("Telegram User:", telegramUser);
    console.log("Backend User:", user);
    console.log("Role:", userRole);
    console.log("Init Data for authorization:", initData ? "Available" : "Not available");
    if (authError) {
      console.warn("Auth Error:", authError);
    }
    if (leadsError) {
      console.warn("Leads Error:", leadsError);
    }
  }, [telegramUser, user, userRole, initData, authError, leadsError]);

  // Handle deep linking via start_param
  useEffect(() => {
    const startParam = getTelegramStartParam();
    console.log('[App] Telegram start_param RAW:', startParam);
    console.log('[App] Telegram WebApp.initDataUnsafe:', WebApp.initDataUnsafe);
    
    if (startParam && startParam.startsWith('lead_')) {
      const leadId = startParam.replace('lead_', '');
      console.log('[App] Deep link detected!');
      console.log('[App] Extracted lead ID:', leadId);
      console.log('[App] Lead ID length:', leadId.length);
      console.log('[App] Lead ID type:', typeof leadId);
      
      setDeepLinkLeadId(leadId);
      setView('lead-detail');
      console.log('[App] View set to lead-detail');
    } else {
      console.log('[App] No deep link detected or invalid format');
      if (startParam) {
        console.log('[App] Start param exists but does not start with "lead_":', startParam);
      }
    }
  }, [setView]);
  
  // Manage Telegram BackButton using custom hook
  useBackButton(view, resetForm, setView);

  // Auth watchdog: if authentication has not resolved (no user, no error, no
  // access-denied) after a grace period, treat it as "stuck" so the loading
  // screen can offer a reload instead of spinning forever (e.g. flaky network
  // or a stale cached client).
  const [authStuck, setAuthStuck] = useState(false);
  useEffect(() => {
    if (user || authError || accessDenied) {
      setAuthStuck(false);
      return;
    }
    const timer = setTimeout(() => setAuthStuck(true), 15000);
    return () => clearTimeout(timer);
  }, [user, authError, accessDenied]);

  // Tiny runtime tests to avoid regressions
  useEffect(() => {
    try {
      const test = [{ notes: 'a\n"b"', id: "1" }];
      const tmp = ["notes"].concat(test.map((t) => (t.notes || "").replaceAll("\n", " ").replaceAll('"', "'"))).join(",");
      console.assert(!tmp.includes("\n"), "CSV should not contain raw newlines in notes");
      const arr = ["B", "A"]; arr.sort(); console.assert(arr[0] === "A", "Array.sort should work");
    } catch (e) {
      console.warn("Self-test failed:", e);
    }
  }, []);



  // Seed demo data once
  useEffect(() => {
    if (leads.length === 0) {
      const now = new Date().toISOString();
      const demo = [
        {
          ...emptyLead,
          id: uid(),
          managerTg: managerTg,
          managerName: managerName,
          event: "AWA Bangkok 25",
          name: "Alex P.",
          company: "AdBoost",
          contact: "@alexp",
          leadType: "Affiliate",
          trafficSource: "TG",
          worksWithUs: "no",
          commissionModel: "CPA",
          photoDataUrls: [],
          notes: "Looking for CPA for DE/AT. Asking for test cap.",
          createdAt: now,
          updatedAt: now,
        },
        {
          ...emptyLead,
          id: uid(),
          managerTg: managerTg,
          managerName: managerName,
          event: "SiGMA Europe 26",
          name: "Mina K.",
          company: "ClickLabs",
          contact: "mina@clicklabs.io",
          leadType: "Agency",
          trafficSource: "Influence",
          worksWithUs: "yes",
          commissionModel: "RevShare",
          photoDataUrls: [],
          notes: "Increase rates for KSA, wants private offer.",
          createdAt: now,
          updatedAt: now,
        },
      ];
      setLeads(demo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1) Access denied (403/401) — shown first so it is never masked by anything else.
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold mb-4">Доступ запрещен</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Вы не добавлены в систему. Обратитесь к администратору для получения доступа.
          </p>
          <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 p-4 rounded-xl">
            <p className="font-semibold mb-2">Ваш Telegram ID:</p>
            <p className="font-mono text-lg">{telegramUser?.id || "Неизвестно"}</p>
            <p className="text-xs mt-2">Сообщите этот ID администратору</p>
          </div>
        </div>
      </div>
    );
  }

  // 2) Authenticating. With a watchdog: if it does not resolve in time, offer a
  //    reload instead of spinning forever.
  if (authLoading) {
    if (authStuck) {
      return (
        <ConnectionTrouble
          title="Долго подключаемся…"
          message="Не удаётся подключиться к серверу. Проверьте соединение и обновите приложение."
          telegramId={telegramUser?.id}
        />
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Загрузка...</p>
        </div>
      </div>
    );
  }

  // 3) Finished loading but no authenticated user (network/5xx error, timeout, or
  //    an anomalous state). Never render the main app without a real user — otherwise
  //    the user sees demo data + an eternal "Connecting…" footer and it looks like a hang.
  if (!user) {
    return (
      <ConnectionTrouble
        title="Нет связи с сервером"
        message={authError || "Не удалось загрузить ваш профиль. Обновите приложение и попробуйте ещё раз."}
        telegramId={telegramUser?.id}
      />
    );
  }

  // 4) Authenticated — render the app. `user` is guaranteed to exist here.
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Topbar telegramUser={telegramUser} managerName={managerName} role={role} setView={setView} />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {view === "home" && (
          <Menu startAdd={startAdd} setView={setView} role={role} leads={leads} exportCsv={exportCsv} />
        )}
        {view === "add" && <LeadForm mode="add" form={form} onChange={onChange} handleSave={handleSave} handleSaveAndNew={handleSaveAndNew} handleSaveAndAI={handleSaveAndAI} resetForm={resetForm} setView={setView} handlePhotos={handlePhotos} removePhoto={removePhoto} handleVoiceNoteUpload={handleVoiceNoteUpload} handleVoiceNoteDelete={handleVoiceNoteDelete} loading={leadsLoading} />}
        {view === "edit" && <LeadForm mode="edit" form={form} onChange={onChange} handleSave={handleSave} resetForm={resetForm} setView={setView} handlePhotos={handlePhotos} removePhoto={removePhoto} handleVoiceNoteUpload={handleVoiceNoteUpload} handleVoiceNoteDelete={handleVoiceNoteDelete} loading={leadsLoading} />}
        {view === "ai-scan" && <AiScanner setView={setView} startAdd={startAdd} />}
        {view === "list" && <LeadList leads={leads} setView={setView} startEdit={startEdit} handleDelete={handleDelete} managerTg={managerTg} role={role} loading={leadsLoading} />}
        {view === "lead-detail" && deepLinkLeadId && <LeadDetail leadId={deepLinkLeadId} loadSingleLead={loadSingleLead} startEdit={startEdit} handleDelete={handleDelete} setView={setView} role={role} />}
        {view === "all-leads" && <AllLeads setView={setView} toast={toast} />}
        {view === "stats" && <Stats setView={setView} />}
        {view === "users" && <AdminPanel setView={setView} />}
        {view === "events" && <EventsPanel setView={setView} />}
        {view === "prompts" && <PromptsManagement setView={setView} />}
        {view === "help" && <Help setView={setView} />}
      </main>
      <footer className="max-w-5xl mx-auto px-4 md:px-6 pb-10 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-center">
          <div>
            <span>✅ Authorized via Backend (Role: {user.role})</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        {/* <DebugPanel /> */}
        <ErrorDisplay />
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}
